// JOBE-0044.10.3: CollectionsProvider — route-level provider that owns
// the single GET /api/collections fetch and broadcasts the result via
// CollectionsContext. Mounted by LibraryListPage as a peer of
// EntityTypesProvider / DocumentKindsProvider so the rail's
// CollectionsRailBlock and the omni palette's AutocompleteSection (the
// `in:` predicate) read a single source of truth.
//
// Tri-state transitions mirror DocumentKindsProvider:
//   - mount → { status: 'loading', items: [], error: null }
//   - fetch ok → { status: 'ready', items: <rows>, error: null }
//   - transport / non-2xx → { status: 'error', items: [], error: '<msg>' }
//
// `retry` re-issues the fetch. Same StrictMode double-effect-invoke
// guard as the sister providers.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CollectionsContext } from './CollectionsContext.js';

const INITIAL_STATE = { status: 'loading', items: [], error: null };

export function CollectionsProvider({ children }) {
  const [state, setState] = useState(INITIAL_STATE);
  const [tick, setTick] = useState(0);
  const fetchedRef = useRef(false);
  // refreshRef distinguishes a `retry()` (reset to loading sentinel) from
  // a `refresh()` (re-fetch in place; preserve current rows so the rail
  // doesn't flash a skeleton on every mutation).
  const refreshRef = useRef(false);

  useEffect(() => {
    if (tick === 0 && fetchedRef.current) return undefined;
    fetchedRef.current = true;
    let alive = true;
    const ctrl = new AbortController();
    if (tick !== 0 && !refreshRef.current) setState(INITIAL_STATE);
    refreshRef.current = false;
    fetch('/api/collections?lifecycle=active', {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) {
          setState({
            status: 'error',
            items: [],
            error: `http_${r.status}`,
          });
          return;
        }
        const j = await r.json();
        if (!alive) return;
        const items = Array.isArray(j?.collections) ? j.collections : [];
        setState({ status: 'ready', items, error: null });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setState({
          status: 'error',
          items: [],
          error: err?.message || String(err),
        });
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [tick]);

  const retry = useCallback(() => {
    refreshRef.current = false;
    setTick((n) => n + 1);
  }, []);

  const refresh = useCallback(() => {
    refreshRef.current = true;
    setTick((n) => n + 1);
  }, []);

  const value = useMemo(
    () => ({ ...state, retry, refresh }),
    [state, retry, refresh],
  );

  return (
    <CollectionsContext.Provider value={value}>
      {children}
    </CollectionsContext.Provider>
  );
}
